import React, { useCallback, useEffect, useState} from "react";
import { Helmet } from "react-helmet";
import {
  Button as MuiButton,
  Card,
  CardContent,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { OrdersPage, Title, SubTitle, NavLink, Hr } from "./style";
import Container from "../../components/ui/ui kit/Container";
import Button from "../../components/ui/ui kit/Button";
import cl from "./Orders.module.sass";
import { useAuth } from "../../hooks/useAuth";
import { CreateLeiService } from "../../api/services/create-lei";
import { ILei } from "../../types/lei";
import Loader from "../../components/ui/Loader";
import { muiTheme } from "../../theme";

const formatDate = (date: string) => date ? date.slice(0, 19).replace("T", " ") : "";

// RapidLEI reports this same "needs LE-RD confirmation" state under two
// different orderStatus names - keep in sync with LERD_CONFIRMATION_STATUSES
// in the backend's utils/utils.py. 'pending_api' is a DIFFERENT, earlier
// state (RapidLEI still loading the profile) and deliberately isn't included
// here - it has no referenceData yet, so showing "Confirm Data" for it would
// send the customer to an empty form.
const LERD_CONFIRMATION_STATUSES = ["pending", "profile_loaded"];
const needsLerdConfirmation = (order_status: string | undefined) =>
  !!order_status && LERD_CONFIRMATION_STATUSES.includes(order_status);

const orderStatusColor = (status: string): "success" | "info" | "default" | "primary" | "error" => {
  if (status === "Complete") return "success";
  if (status === "Manual") return "info";
  if (status === "Archived") return "default";
  if (status === "Submitted") return "primary";
  return "error";
};

export const Orders = () => {
  const [isCreationsShow, setCreationsShow] = useState<boolean>(false);
  const { isAuth } = useAuth();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [orders, setOrders] = useState<ILei[]>([]);
  const [loading, setLoading] = useState(false);
  const [isItemLoading, setIsItemLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuOrderId, setMenuOrderId] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const res = await CreateLeiService.requestOrderList();
        const lei = res.data.map((el: any) => ({
          order_id: el.id,
          order_type: el.order_type,
          lei: el.order_tracking_code,
          status: el.status,
          order_status: el.order_status,
          date: el.created_at,
          name: el.company_name,
          country: el.legal_jurisdiction,
        }));
        setOrders(lei);
      } catch (error) {
        console.log('--> Error in getting orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleCheckStatus = useCallback(async (orderTrackingCode: string, index: number, orderId?: number) => {
    let requestBody: any;
    if (orderTrackingCode) {
      requestBody = {
        order_tracking_code: orderTrackingCode
      }
    } else {
      requestBody = {
        order_id: orderId
      };
    }

    setSelectedOrderId(orderId || 0);
    setIsItemLoading(true);

    try {
      const res = await CreateLeiService.checkOrderStatus(requestBody);

      if (res.data.data) {
        const el = res.data.data;
        const orderItem = {
          order_id: el.id,
          order_type: el.order_type,
          lei: el.order_tracking_code,
          status: el.status,
          order_status: el.order_status,
          date: el.created_at,
          name: el.company_name,
          country: el.legal_jurisdiction,
        };

        setOrders((prev) => {
          if (index < 0 || index >= prev.length) return prev;
          const next = [...prev];
          next.splice(index, 1, orderItem);
          return next;
        });
      }
    } catch (e) {
      console.log('-> Error in handleCheckStatus:', e);
    } finally {
      setIsItemLoading(false);
      setSelectedOrderId(0);
    }
  }, []);

  const renderActions = (order_id: number | undefined, lei: string, index: number) => {
    if (isItemLoading && selectedOrderId === order_id) {
      return (
        <div className={cl["loading-wrapper"]}>
          <Loader size={18} center={false} />
        </div>
      );
    }

    return (
      <>
        <MuiButton
          size="small"
          variant="outlined"
          onClick={(e) => {
            setMenuAnchor(e.currentTarget);
            setMenuOrderId(order_id ?? null);
          }}
        >
          Actions
        </MuiButton>
        <Menu
          anchorEl={menuAnchor}
          open={menuOrderId === order_id}
          onClose={() => setMenuOrderId(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuOrderId(null);
              handleCheckStatus(lei, index, order_id);
            }}
          >
            Update the Status
          </MenuItem>
        </Menu>
      </>
    );
  };

  const renderLerdBanner = (order_id: number | undefined, order_status: string | undefined) => {
    if (!needsLerdConfirmation(order_status)) {
      return null;
    }

    return (
      <div className={cl["confirm_lerd-banner"]}>
        <span>Your Legal Entity Reference Data is ready — please confirm it to continue.</span>
        <Button
          styleType="primary"
          component={NavLink}
          to={`/lerd?order_id=${order_id}`}
          width="auto"
          height="34px"
          noMarginTop
        >
          Confirm Data
        </Button>
      </div>
    );
  };

  return (
    <Container>
      <Helmet> {/* Add the Helmet component */}
        <title>Orders submitted to LEIpapa by the applicant</title>
        <meta
          name="description"
          content="Orders submitted to LEIpapa. LEIpapa is an official LEI Registration Agent of Ubisecure RapidLEI. Press Create LEI Application to Register, Renew or Transfer an LEI."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {isAuth && (
        <OrdersPage>
          <div className={cl["title_wrap"]}>
            <Title>Orders</Title>
          </div>
          <SubTitle>
            Press "Create LEI Application"👇 to Register, Renew or Transfer an LEI.
          </SubTitle>
          <div className={cl["create_lei-wrap"]}>
            <div className={cl["inner_button-lei"]}>
              <button
                className={cl["button"]}
                onClick={() => setCreationsShow((prev) => !prev)}
              >
                Create LEI Application
              </button>
            </div>
            {isCreationsShow && (
              <div className={cl["create_leicode"]}>
                <div className={cl["hidden_top"]}>
                  <div className={cl["wrap_button"]}>
                    <Button styleType="primary" component={NavLink} to="/create" width="auto" height="34px" noMarginTop>
                      Create new LEI
                    </Button>
                  </div>
                  <p className={cl["top_text"]}>
                    Submit your request using a standard web template. This
                    option is useful for single or low volume submissions.
                  </p>
                </div>

                <Hr />

                <div className={cl["hidden_bottom"]}>
                  <div className={cl["wrap_button"]}>
                    <Button styleType="primary" component={NavLink} to="/renew" width="auto" height="34px" noMarginTop>
                      Renew LEI
                    </Button>
                  </div>
                  <p className={cl["bottom_text"]}>
                    Renew here any existing LEI. To keep an LEI active, it must be renewed every year. We use a fast, automated LEI renewal process.
                  </p>
                </div>

                <Hr />

                <div className={cl["hidden_bottom"]}>
                  <div className={cl["wrap_button"]}>
                    <Button styleType="primary" component={NavLink} to="/transfer" width="auto" height="34px" noMarginTop>
                      Transfer LEI
                    </Button>
                  </div>
                  <p className={cl["bottom_text"]}>
                    Transfer your LEI to LEIpapa for full service. If another service provider manages your LEI, you can transfer it to LEIpapa for free.
                  </p>
                </div>
              </div>
            )}

            <SubTitle>
              Below is the list of your orders (if any).
            </SubTitle>

            <Hr />

            <div className={cl["table_wrap"]}>
              {loading ? (
                <Loader />
              ) : orders.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                  No data found
                </Typography>
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {orders.map(({ order_id, order_type, lei, name, country, status, order_status, date }, index) => (
                    <React.Fragment key={order_id ?? index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {order_type}
                            </Typography>
                            <Chip size="small" label={status || "n/a"} color={orderStatusColor(status)} />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {name}
                          </Typography>
                          {status === "Complete" ? (
                            <Typography variant="body2">
                              <NavLink to={`/order-details/${lei}`} style={{ color: "#4B7196", fontWeight: 500 }}>
                                {lei}
                              </NavLink>
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {lei}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {country} {date && `· ${formatDate(date)}`}
                          </Typography>
                          <Stack direction="row" sx={{ mt: 1.5 }}>
                            {renderActions(order_id, lei, index)}
                          </Stack>
                        </CardContent>
                      </Card>
                      {renderLerdBanner(order_id, order_status)}
                    </React.Fragment>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Type</TableCell>
                        <TableCell>Order Tracking Code</TableCell>
                        <TableCell>Legal Name</TableCell>
                        <TableCell align="center">Country</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Order Creation Date</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.map(({ order_id, order_type, lei, name, country, status, order_status, date }, index) => (
                        <React.Fragment key={order_id ?? index}>
                          <TableRow hover>
                            <TableCell>{order_type}</TableCell>
                            <TableCell>
                              {status === "Complete" ? (
                                <NavLink to={`/order-details/${lei}`} style={{ color: "#4B7196", fontWeight: 500 }}>
                                  {lei}
                                </NavLink>
                              ) : (
                                lei
                              )}
                            </TableCell>
                            <TableCell>{name}</TableCell>
                            <TableCell align="center">{country}</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={status || "n/a"} color={orderStatusColor(status)} />
                            </TableCell>
                            <TableCell align="center">{formatDate(date)}</TableCell>
                            <TableCell align="right">{renderActions(order_id, lei, index)}</TableCell>
                          </TableRow>
                          {needsLerdConfirmation(order_status) && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ borderBottom: "1px solid rgba(0,0,0,.125)" }}>
                                {renderLerdBanner(order_id, order_status)}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          </div>
        </OrdersPage>
      )}
    </Container>
  );
};
